import React from 'react';
import {observer} from 'mobx-react';
import {Report} from '../types/Report';
import Modal from '../../Modal';
import {I18n, TProvider} from '../../i18n/i18n';
import {stylesheet} from 'typestyle';
import {GreenColor} from '../../styles/colors';
import {important, rgba} from 'csx';
import {inject} from '../../inject';
import Store from '../../stores/store';
import {LoadingHover} from '../../views/Legend/TermItem';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';

interface ChooseFromDefaultTemplateModalProps {
    defaultReportModalIsOpen: boolean;
    setDefaultReportModalIsOpen: (open: boolean) => void;
    defaultReports: Report[];
    store: Store;
    saveNewReport: (report: Report) => Promise<void>;
    editReport: (report: Report) => void;
}

@observer
class ChooseFromDefaultTemplateModal extends React.Component<ChooseFromDefaultTemplateModalProps> {
    public render() {
        if (!this.props.defaultReportModalIsOpen) {
            return null;
        }

        return (
            <Modal
                store={this.props.store}
                modalHeader={<I18n i18nKey={'report:makeReportFromTemplate'} />}
                modalBody={this.props.defaultReports.map((defaultReport) => (
                    <DefaultTemplateRow
                        key={defaultReport.id + defaultReport.name}
                        defaultReport={defaultReport}
                        saveNewReport={this.props.saveNewReport}
                        setDefaultReportModalIsOpen={this.props.setDefaultReportModalIsOpen}
                        editReport={this.props.editReport}
                    />
                ))}
                onModalClose={() => this.props.setDefaultReportModalIsOpen(false)}
            />
        );
    }
}

interface DefaultTemplateRowProps {
    defaultReport: Report;
    saveNewReport: (report: Report) => Promise<void>;
    editReport: (report: Report) => void;
    setDefaultReportModalIsOpen: (open: boolean) => void;
}

class DefaultTemplateRow extends React.Component<DefaultTemplateRowProps, {isSaving: boolean}> {
    constructor(props: DefaultTemplateRowProps) {
        super(props);
        this.state = {
            isSaving: false,
        };
    }

    private openDefaultTemplateInEditor = (report: Report) => {
        this.props.setDefaultReportModalIsOpen(false);
        this.props.editReport(report);
    };

    private saveDefaultTemplateAsReport = (report: Report) => {
        this.setState({isSaving: true});
        this.props
            .saveNewReport(report)
            .then(() => {
                this.setState({isSaving: false});
                this.props.setDefaultReportModalIsOpen(false);
            })
            .catch(() => {
                this.setState({isSaving: false});
            });
    };

    public render = () => {
        const {defaultReport} = this.props;
        return (
            <div className={templateCss.reportTemplateLine}>
                <div className={templateCss.reportTemplateText}>{defaultReport.name}</div>
                <div className={templateCss.reportTemplateActions}>
                    <TProvider>
                        {(t) => (
                            <button
                                title={t('data.edit')}
                                className={templateCss.reportTemplateActionButton}
                                onClick={() => this.openDefaultTemplateInEditor(defaultReport)}>
                                <FontAwesomeIcon icon="edit" />
                            </button>
                        )}
                    </TProvider>
                    <TProvider>
                        {(t) => (
                            <button
                                title={t('data.save')}
                                className={templateCss.reportTemplateActionButton}
                                onClick={() => this.saveDefaultTemplateAsReport(defaultReport)}>
                                <FontAwesomeIcon icon="save" />
                                {this.state.isSaving ? <LoadingHover /> : null}
                            </button>
                        )}
                    </TProvider>
                </div>
            </div>
        );
    };
}

export default inject('store')(ChooseFromDefaultTemplateModal);

const templateCss = stylesheet({
    reportFromTemplateButton: {
        marginTop: 15,
        cursor: 'pointer',
        outline: 'none',
        border: 'none',
        padding: 5,
        borderRadius: 2,
        color: 'white',
        background: GreenColor,
    },
    reportTemplateLine: {
        display: 'flex',
        alignItems: 'center',
        padding: '5px 0px',
        borderBottom: '1px solid #ddd',
        $nest: {
            '&:last-child': {
                borderBottom: important('none'),
            },
        },
    },
    reportTemplateText: {
        flexGrow: 1,
    },
    reportTemplateActions: {
        display: 'flex',
        opacity: 0.6,
    },
    reportTemplateActionButton: {
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 50,
        height: 50,
        backgroundColor: 'transparent',
        position: 'relative',
        outline: 'none',
        border: 'none',
        transition: '.2s',
        borderRadius: '100%',
        $nest: {
            '&:hover': {
                backgroundColor: rgba(0, 0, 0, 0.1).toString(),
            },
        },
    },
});



// WEBPACK FOOTER //
// ./src/report/views/ChooseFromDefaultTemplateModal.tsx